import Checklist from '@repo/datalayer/store/models/Checklist'
import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import { Button } from '../../components/ui/button'
import { ChecklistItemView } from './ChecklistItemView'
import { ConfirmReviewDialog } from './ConfirmReviewDialog'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { useRouter } from '@/hooks/router'
import { useTranslation } from 'react-i18next'
import { AssignPersonDialog } from './AssignPersonDialog'
import { RunnerDialog } from './RunnerDialog'
import { useCurrentUser } from '@/auth'
import { Plus, CheckCircle, Trash2, ArrowLeft } from 'lucide-react'

function CircularProgress({
  percent,
  size = 80,
}: {
  percent: number
  size?: number
}) {
  const radius = (size - 8) / 2
  const circumference = 2 * Math.PI * radius

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="-rotate-90"
        style={{ width: size, height: size }}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          className="text-white/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          className="text-white transition-all duration-700"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: circumference - (percent / 100) * circumference,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span data-test-id="checklist-progress" className="text-xl font-bold text-white">
          {Math.round(percent)}%
        </span>
      </div>
    </div>
  )
}

export const LiveChecklistView = observer(function LiveChecklistView({
  checklist,
}: {
  checklist: Checklist
}) {
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const user = useCurrentUser()
  const currentUserIsCreator = user.id === checklist.creatorId
  const { t } = useTranslation()

  const total = checklist.items.length
  const completed = checklist.items.filter(
    (i) => i.complete || i.skipped
  ).length
  const percent = total > 0 ? (completed / total) * 100 : 0

  return (
    <main
      className="max-w-[900px] mx-auto w-full overflow-auto relative"
      ref={containerRef}
    >
      <div className="px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => router.navigateToHome()}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Home</span>
        </button>

        {/* Hero Header */}
        <div className="bg-forest rounded-3xl p-8 mb-6 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <CircularProgress percent={percent} />
            <div className="flex-1">
              <h1
                data-test-id="checklist-title"
                className="text-2xl md:text-3xl font-bold mb-2"
              >
                {checklist?.name}
              </h1>
              <p className="text-white/80 text-lg">
                {completed} of {total} items completed
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="relative flex flex-wrap items-center gap-3 mt-6 pt-6 border-t border-white/20">
            <RunnerDialog checklist={checklist} initialItemIndex={0}>
              ko
            </RunnerDialog>
            <AssignPersonDialog
              assigneeId={checklist.assigneeId}
              onSelect={(userId) => {
                checklist.assign(userId)
              }}
            />
            {checklist?.allDone && (
              <Button
                data-test-id="finish-checklist-button"
                onClick={() => checklist?.setAsComplete()}
                className="bg-cream text-forest hover:bg-cream/90"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {t('checklists.finish')}
              </Button>
            )}
            {!checklist?.allDone && (
              <ConfirmReviewDialog
                onConfirm={() => {
                  checklist?.setAsComplete()
                }}
              >
                <Button
                  data-test-id="finish-checklist-button"
                  variant="secondary"
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('checklists.finish')}
                </Button>
              </ConfirmReviewDialog>
            )}
            {currentUserIsCreator && (
              <ConfirmDialog
                destructive
                confirmLabel={t('delete')}
                title={t('areYouSure')}
                description={t('checklists.deleteConfirmation')}
                onConfirm={async () => {
                  checklist.delete().then(() => {
                    router.navigateToHome()
                  })
                }}
              >
                <Button
                  variant="secondary"
                  className="bg-white/20 hover:bg-red-500 text-white border-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </ConfirmDialog>
            )}
          </div>
        </div>

        {/* Checklist Items */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y">
            {checklist?.sortedItems.map((item) => (
              <ChecklistItemView
                key={item.id}
                checklist={checklist}
                item={item}
              />
            ))}
          </div>

          {/* Add new item */}
          <div className="border-t bg-muted/30">
            <form
              className="flex items-center gap-3 p-4"
              onSubmit={(e) => {
                e.preventDefault()

                const target = e.target as HTMLFormElement
                const data = new FormData(target)
                const name = data.get('name') as string

                if (name) {
                  checklist?.addItem(name)
                  target.reset()
                  setTimeout(() => {
                    containerRef.current?.scrollTo({
                      top: containerRef.current.scrollHeight,
                      behavior: 'instant',
                    })
                  }, 100)
                }
              }}
            >
              <div className="p-2 rounded-lg bg-primary/10">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <input
                type="text"
                name="name"
                data-test-id="add-item-input"
                placeholder={t('checklists.addNewItem')}
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                autoComplete="off"
                data-1p-ignore
              />
              <Button
                type="submit"
                size="sm"
                data-test-id="add-item-submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t('checklists.add')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </main>
  )
})
